import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Box } from '@/components/Box/Box';
import { Button } from '@/components/Button/Button';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormMessage, FormTitle } from '@/bundle/_OrgAdmin/shared/ui/styles';
import {
  ASSIGN_USER_INITIAL_VALUES,
  ASSIGN_USER_SCHEMA,
  AssignUserFormType,
} from '@/bundle/_OrgAdmin/shared/const/const';
import { useFormik } from 'formik';
import { DetailsWidget } from '@/components/DetailsWidget/DetailsWidget';
import { ChangeButton } from './styles';
import RecipientChainProxyIcon from '@/images/RecipientChainProxyIcon.svg?react';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import {
  PROXY_ASSIGN_RECIPIENT_ERROR_AMPLITUDE_EVENTS_MAP,
  PROXY_ASSIGN_RECIPIENT_SUCCESS_AMPLITUDE_EVENTS_MAP,
} from './const';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { PaymentDetailsHeaderSlot } from '@/bundle/shared/components/PaymentDetailsHeaderSlot/PaymentDetailsHeaderSlot';
import { FormikPhoneInput } from '@/components/form/fields/FormikPhoneInput/FormikPhoneInput';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { getIsAvailableToAssign } from '@/helpers/userHelpers';
import { ProxySearchUserEmailInput } from '@/bundle/_Recipient/_Details/ui/ProxySearchUserEmailInput/ProxySearchUserEmailInput';
import { useAssignUser } from '@/bundle/shared/hooks/useAssignUser';
import { HTTP_BAD_REQUEST_STATUS } from '@/api/const';
import {
  assignRecipientByProxy,
  createRecipientByProxy,
  loadUserDetailsByProxy,
  recipientAssignmentStatusByProxy,
} from '@/bundle/_Recipient/_Details/ui/ProxyAssignRecipientForm/api/api';

type ProxyAssignRecipientFormType = {
  onCreateRecipient: () => void;
  onAssignRecipient: () => void;
  onChangeProvidePaymentDetailsMode: () => void;
  isDocumentVerificationRequired: boolean;
};

export const ProxyAssignRecipientForm = ({
  onCreateRecipient,
  onAssignRecipient,
  onChangeProvidePaymentDetailsMode,
  isDocumentVerificationRequired,
}: ProxyAssignRecipientFormType) => {
  const { id } = useParams<{ id: string }>();
  const [submittedEmailRequest, setSubmittedEmailRequest] = useState(false);
  const [assignedUserId, setAssignedUserId] = useState<null | string>(null);

  const { data: assignmentStatusData } = useQuery({
    queryKey: ['load_assignment_status_by_proxy', id, assignedUserId],
    queryFn: () => recipientAssignmentStatusByProxy(id, assignedUserId),
    enabled: !!assignedUserId,
  });

  const { data: loadUserData } = useQuery({
    queryKey: ['load_user_by_proxy', assignedUserId],
    queryFn: () => loadUserDetailsByProxy(assignedUserId),
    enabled: !!assignedUserId && !!assignmentStatusData?.body?.is_available_to_assign,
  });

  const {
    mutate: createRecipientMutate,
    isPending: isPendingCreateRecipient,
    data: createRecipientData,
    reset: resetCreateRecipient,
  } = useMutation({
    mutationKey: ['create_recipient_by_proxy'],
    mutationFn: (values: AssignUserFormType) => {
      //TODO: is proxy ???
      return createRecipientByProxy(id, { ...values, is_proxy: false });
    },
    onSuccess(response) {
      if (response?.error) return;

      amplitudeService.logEvent(AMPLITUDE_EVENTS.ProxyAssignNewRecipient);
      onCreateRecipient();
    },
  });

  const {
    mutate: assignRecipientMutate,
    isPending: isPendingAssignRecipient,
    data: assignRecipientData,
    reset: resetAssignRecipient,
  } = useMutation({
    mutationKey: ['assign_recipient_by_proxy'],
    mutationFn: () => {
      const userData = loadUserData?.body;

      //TODO: is proxy ???
      const payload = {
        user_id: userData?.id,
      };

      return assignRecipientByProxy(id, payload);
    },
    onSuccess(response) {
      if (response?.error) return;

      const eventType = PROXY_ASSIGN_RECIPIENT_SUCCESS_AMPLITUDE_EVENTS_MAP[assignmentStatusData?.body?.code];

      if (eventType) {
        amplitudeService.logEvent(eventType);
      }

      onAssignRecipient();
    },
  });

  useEffect(() => {
    const assignmentStatusBody = assignmentStatusData?.body;

    if (assignmentStatusBody && !assignmentStatusBody?.is_available_to_assign) {
      const eventType = PROXY_ASSIGN_RECIPIENT_ERROR_AMPLITUDE_EVENTS_MAP[assignmentStatusData?.body?.code];

      if (eventType) {
        amplitudeService.logEvent(eventType);
      }
    }
  }, [assignmentStatusData?.body]);

  const isAvailableToAssign = getIsAvailableToAssign(assignmentStatusData?.body?.is_available_to_assign);
  const user = loadUserData?.body;

  const createRecipientError = createRecipientData?.error;
  const assignRecipientError = assignRecipientData?.error;
  const apiError = createRecipientError || assignRecipientError;
  const hasApiError = apiError?.statusCode === HTTP_BAD_REQUEST_STATUS;
  const isLoading = isPendingCreateRecipient || isPendingAssignRecipient;

  const isDisabledForm = !!assignedUserId || !submittedEmailRequest || hasApiError;
  const isDisabledValidation = !!user || (assignedUserId && !isAvailableToAssign);
  const isDisabledAssign = !isAvailableToAssign || !submittedEmailRequest || hasApiError;

  const formik = useFormik<AssignUserFormType>({
    initialValues: ASSIGN_USER_INITIAL_VALUES,
    validationSchema: isDisabledValidation ? null : ASSIGN_USER_SCHEMA,
    onSubmit: (values) => {
      if (assignedUserId) {
        assignRecipientMutate();

        return;
      }

      createRecipientMutate(values);
    },
  });

  const searchUserEmail = (userId: string) => {
    formik.setErrors({});
    setAssignedUserId(userId);
    setSubmittedEmailRequest(true);
  };

  const submitUserEmail = () => {
    if (!formik.touched.email && formik.errors?.email) {
      formik.setTouched({ email: true });
    }
  };

  const onResetData = () => {
    setAssignedUserId(null);
    setSubmittedEmailRequest(false);
    resetCreateRecipient();
    resetAssignRecipient();
    formik.resetForm();
    formik.setFieldValue('email', formik.values.email);
  };

  useAssignUser(
    loadUserData?.body,
    formik,
    onResetData,
    assignedUserId,
    submittedEmailRequest,
    assignmentStatusData?.body?.is_available_to_assign,
    apiError
  );

  const assignmentStatus = assignmentStatusData?.body?.code;

  return (
    <DetailsWidget
      hasHighlight
      headlineSlot={
        <PaymentDetailsHeaderSlot
          icon={<RecipientChainProxyIcon />}
          header='Invite the Recipient'
          meta={<ChangeButton onClick={onChangeProvidePaymentDetailsMode}>Change</ChangeButton>}
        />
      }
    >
      <FormTitle>Assign Recipient</FormTitle>
      <FormMessage>Please indicate who will be receiving this wire sending.</FormMessage>
      <FormikForm value={formik}>
        <ProxySearchUserEmailInput
          wireId={id}
          assignmentStatus={assignmentStatus}
          email={formik.values.email}
          onSearch={searchUserEmail}
          onSubmit={submitUserEmail}
          error={formik.errors?.email}
          apiError={apiError}
          labelSlot={isDocumentVerificationRequired && 'ID VERIFICATION REQUIRED'}
        />

        <FormikPhoneInput
          name='mobile_phone'
          label='Mobile Phone Number*'
          disabled={isDisabledForm}
          apiError={apiError}
        />
        <FormikInput
          name='first_name'
          label='First Name*'
          placeholder='Enter First Name'
          disabled={isDisabledForm}
          apiError={apiError}
        />
        <FormikInput
          name='middle_name'
          label='Middle Name'
          placeholder='Enter Middle Name'
          disabled={isDisabledForm}
          apiError={apiError}
        />
        <FormikInput
          name='last_name'
          label='Last Name*'
          placeholder='Enter Last Name'
          disabled={isDisabledForm}
          apiError={apiError}
        />

        <Box alignItems='center' justifyContent='flex-end' marginTop='14px'>
          <Button
            width={172}
            size='medium'
            type='submit'
            disabled={isDisabledAssign}
            mobileStretch
            isLoading={isLoading}
          >
            Assign
          </Button>
        </Box>
      </FormikForm>
    </DetailsWidget>
  );
};
